import type { FunctionComponent } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import Link from '@atlaskit/link';
import { xcss, Box } from '@atlaskit/primitives';
import {
  ATLASSIAN_PRIVACY_POLICY_URL,
  ATLASSIAN_TERMS_OF_USE_URL,
} from '../urls';

interface LegalDisclaimerMessages {
  agreementHeader: MessageDescriptor;
  /** The message containing {privacyPolicyLink} and {termsOfUseLink} placeholders. */
  agreementLinks: MessageDescriptor;
  privacyPolicy: MessageDescriptor;
  termsOfUse: MessageDescriptor;
}

export interface LegalDisclaimerProps {
  messages: LegalDisclaimerMessages;
  /** Optional product name interpolated into {brand} in agreementHeader. */
  product?: string;
}

const disclaimerStyle = xcss({
  color: 'color.text.subtle',
  font: 'font.body.small',
});

const compactParagraphStyle = xcss({
  margin: 'space.0',
});

const LegalDisclaimer: FunctionComponent<LegalDisclaimerProps> = ({
  messages,
  product,
}) => {
  const privacyPolicyLink = (
    <Link href={ATLASSIAN_PRIVACY_POLICY_URL}>
      <FormattedMessage {...messages.privacyPolicy} />
    </Link>
  );

  const termsOfUseLink = (
    <Link href={ATLASSIAN_TERMS_OF_USE_URL}>
      <FormattedMessage {...messages.termsOfUse} />
    </Link>
  );

  return (
    <Box xcss={disclaimerStyle}>
      <Box as="p" xcss={compactParagraphStyle}>
        <FormattedMessage {...messages.agreementHeader} values={{ product }} />{' '}
        <FormattedMessage
          {...messages.agreementLinks}
          values={{ privacyPolicyLink, termsOfUseLink }}
        />
      </Box>
    </Box>
  );
};

export default LegalDisclaimer;
